<template>
  <div class="ccp" @mouseenter="hover = true" @mouseleave="hover = false" @focusin="hover = true" @focusout="hover = false">
    <a class="ccp-link" :href="href" target="_blank" rel="noopener"
       title="Clockwork Coliseum — a web auto-battler from the people who build this site">
      <span class="ccp-head">
        <img class="ccp-logo" :src="logo" alt="" width="46" height="46">
        <span class="ccp-names">
          <span class="ccp-crew">From the wiznaib.us crew</span>
          <span class="ccp-title">Clockwork Coliseum</span>
        </span>
      </span>
      <!-- Always on screen, so every slide reads as news from another game, not FFTBG's. -->
      <span class="ccp-tagline">A 24/7 FFT-style auto-battler, right in your browser</span>
      <span class="ccp-rule"></span>
      <transition name="ccp-fade" mode="out-in">
        <span class="ccp-slide" :key="slide.key">
          <span class="ccp-kicker">{{ slide.kicker }}</span>
          <span class="ccp-big">{{ slide.title }}</span>
          <span class="ccp-sub">{{ slide.sub }}</span>
          <span v-if="slide.detail" class="ccp-detail">{{ slide.detail }}</span>
        </span>
      </transition>
      <span class="ccp-cta">Enter the arena →</span>
    </a>
    <div v-if="slides.length > 1" class="ccp-dots" role="group" aria-label="Clockwork Coliseum news">
      <button v-for="(s, i) in slides" :key="s.key" type="button" class="ccp-dot" :class="{ on: i === index }"
              :aria-label="s.kicker" :aria-current="i === index ? 'true' : null" @click="index = i"></button>
    </div>
  </div>
</template>

<script>
// The Clockwork Coliseum promo under the Champ Match cell. Live news from the game's public
// API (the reigning champion, the fight on screen, the season race…) rotates through it;
// the slide text is built in lib/coliseumPromo.js.
import { API, buildSlides, withFallback } from '../lib/coliseumPromo.js';

const SITE = 'https://clockworkcoliseum.com';
const ROTATE_MS = 7000;
const REFRESH_MS = 3 * 60_000;
// Each slide opens the page it's about.
const PAGE = { champ: '/champions', race: '/seasons', toc: '/seasons' };
const FONTS = 'https://fonts.googleapis.com/css2?family=Cinzel:wght@700&family=Barlow+Condensed:wght@600;700&display=swap';

async function getJson(path) {
  const r = await fetch(API + path, { signal: AbortSignal.timeout(8000) });
  if (!r.ok) throw new Error(`${path}: HTTP ${r.status}`);
  return r.json();
}

export default {
  name: 'ColiseumPromo',
  data() {
    return {
      data: {},
      index: 0,
      hover: false,
      fetchedAt: 0,
      logo: new URL('../../assets/images/cc-logo.png', import.meta.url).href,
    };
  },
  computed: {
    slides() {
      return withFallback(buildSlides(this.data));
    },
    slide() {
      return this.slides[this.index % this.slides.length];
    },
    href() {
      return SITE + (PAGE[this.slide.key] || '/');
    },
  },
  mounted() {
    // The game's own typefaces (Cinzel, Barlow Condensed), once per page.
    if (!document.querySelector('link[data-ccp-fonts]')) {
      const l = document.createElement('link');
      l.rel = 'stylesheet';
      l.href = FONTS;
      l.dataset.ccpFonts = '';
      document.head.appendChild(l);
    }
    this.refresh();
    this.rotateTimer = setInterval(() => {
      if (!this.hover && document.visibilityState === 'visible' && this.slides.length > 1) {
        this.index = (this.index + 1) % this.slides.length;
      }
    }, ROTATE_MS);
    this.refreshTimer = setInterval(() => this.refresh(), REFRESH_MS);
    this.onVisible = () => {
      if (document.visibilityState === 'visible' && Date.now() - this.fetchedAt > REFRESH_MS) this.refresh();
    };
    document.addEventListener('visibilitychange', this.onVisible);
  },
  beforeUnmount() {
    clearInterval(this.rotateTimer);
    clearInterval(this.refreshTimer);
    document.removeEventListener('visibilitychange', this.onVisible);
  },
  methods: {
    // Each endpoint on its own: one failing just drops its slide. A hidden tab doesn't poll.
    async refresh() {
      if (document.visibilityState !== 'visible' && this.fetchedAt) return;
      this.fetchedAt = Date.now();
      const [state, champion, pulse, season] = await Promise.allSettled(
        ['/state', '/champion', '/pulse', '/season'].map(getJson),
      );
      const val = (x) => (x.status === 'fulfilled' ? x.value : undefined);
      this.data = { state: val(state), champion: val(champion), pulse: val(pulse), season: val(season) };
    },
  },
};
</script>

<style scoped>
/* The Coliseum's own look: Cinzel + Barlow Condensed, brass on a dark navy panel with
   gilt corner brackets (its --panel / --brass / --brackets tokens). */
.ccp {
  --brass: #d9b45e;
  --brass-hi: #f3d68e;
  --frame-hi: #e8cf8c;
  --cond: 'Barlow Condensed', 'Arial Narrow', system-ui, sans-serif;
  --display: 'Cinzel', Georgia, 'Times New Roman', serif;
  position: relative;
  width: 270px;
  box-sizing: border-box;
  padding: 12px 14px 10px;
  border: 1px solid #7a5f2e;
  border-radius: 4px;
  color: #cdd3dd;
  text-align: left;
  line-height: 1.3;
  background:
    linear-gradient(var(--frame-hi), var(--frame-hi)) left 0 top 0 / 12px 2px no-repeat,
    linear-gradient(var(--frame-hi), var(--frame-hi)) left 0 top 0 / 2px 12px no-repeat,
    linear-gradient(var(--frame-hi), var(--frame-hi)) right 0 top 0 / 12px 2px no-repeat,
    linear-gradient(var(--frame-hi), var(--frame-hi)) right 0 top 0 / 2px 12px no-repeat,
    linear-gradient(var(--frame-hi), var(--frame-hi)) left 0 bottom 0 / 12px 2px no-repeat,
    linear-gradient(var(--frame-hi), var(--frame-hi)) left 0 bottom 0 / 2px 12px no-repeat,
    linear-gradient(var(--frame-hi), var(--frame-hi)) right 0 bottom 0 / 12px 2px no-repeat,
    linear-gradient(var(--frame-hi), var(--frame-hi)) right 0 bottom 0 / 2px 12px no-repeat,
    linear-gradient(180deg, #1b2131, #0e121c);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.45);
}
.ccp-link { display: flex; flex-direction: column; color: inherit; text-decoration: none; }
.ccp-link:focus-visible { outline: 2px solid var(--brass-hi); outline-offset: 4px; }

.ccp-head { display: flex; align-items: center; gap: 10px; }
.ccp-logo { width: 46px; height: 46px; flex: none; image-rendering: pixelated; filter: drop-shadow(0 2px 0 rgba(0, 0, 0, 0.5)); }
.ccp-names { display: flex; flex-direction: column; min-width: 0; }
.ccp-crew {
  font-family: var(--cond); font-weight: 600; font-size: 11px;
  text-transform: uppercase; letter-spacing: 2.4px; color: var(--brass);
}
.ccp-title {
  font-family: var(--display); font-weight: 700; font-size: 19px; line-height: 1.1;
  background: linear-gradient(#fff3cf, #e2bd6a 55%, #a27a35);
  -webkit-background-clip: text; background-clip: text; color: transparent;
  filter: drop-shadow(0 2px 0 rgba(0, 0, 0, 0.45));
}
.ccp-tagline {
  margin-top: 6px; font-size: 12.5px; font-style: italic; line-height: 1.25; color: #e6dcc3;
}
.ccp-rule {
  display: block; height: 1px; margin: 8px 0 8px;
  background: linear-gradient(90deg, transparent, rgba(217, 180, 94, 0.7), transparent);
}

/* Fixed height so rotating slides of different lengths don't make the card jump. */
.ccp-slide { display: flex; flex-direction: column; gap: 3px; min-height: 156px; }
.ccp-kicker {
  font-family: var(--cond); font-weight: 700; font-size: 12px;
  text-transform: uppercase; letter-spacing: 1.4px; color: var(--brass);
}
.ccp-big {
  font-family: var(--cond); font-weight: 700; font-size: 21px; line-height: 1.1;
  color: var(--brass-hi); overflow-wrap: anywhere;
}
.ccp-sub { font-size: 12.5px; color: #cdd3dd; }
.ccp-detail { font-size: 11px; color: #8b93a3; }

.ccp-cta {
  align-self: stretch; margin-top: 10px; padding: 7px 14px; text-align: center;
  font-family: var(--cond); font-weight: 700; font-size: 16px;
  text-transform: uppercase; letter-spacing: 1.5px; color: #1a1408;
  background: linear-gradient(#e8cf8c, #9c7a3a);
  clip-path: polygon(10px 0, 100% 0, calc(100% - 10px) 100%, 0 100%);
  transition: filter 0.15s;
}
.ccp-link:hover .ccp-cta { filter: brightness(1.12); }

.ccp-dots { display: flex; justify-content: center; gap: 7px; margin-top: 9px; }
.ccp-dot {
  width: 8px; height: 8px; padding: 0; border: 1px solid var(--brass); background: transparent;
  transform: rotate(45deg); cursor: pointer;
}
.ccp-dot.on { background: var(--brass-hi); border-color: var(--brass-hi); }
.ccp-dot:focus-visible { outline: 2px solid var(--brass-hi); outline-offset: 2px; }

.ccp-fade-enter-active, .ccp-fade-leave-active { transition: opacity 0.35s, transform 0.35s; }
.ccp-fade-enter-from { opacity: 0; transform: translateY(4px); }
.ccp-fade-leave-to { opacity: 0; transform: translateY(-4px); }
@media (prefers-reduced-motion: reduce) {
  .ccp-fade-enter-active, .ccp-fade-leave-active { transition: none; }
}
</style>
